// #region imports

// --- Angular
import { HttpErrorResponse } from "@angular/common/http";

// --- HS Code
import { ErrorMessage } from '../system/error-message';

// #endregion

export interface IApplicationError {
  errorCode: number;          // System error number
  errorHeader: string;        // The header text to show in the error message box
  errorMessage: string;       // System error message
  displayMessage: string;     // Message to display to users
  source: string | null;      // The component that raised the error
  isFatal: boolean;           // A flag indicating whether or not the error can be recovered from
  statusCode: number;         // A context specific code
  detailedMessage: string;    // A detailed break-down of the error
  redirectTo: string | null | undefined;  // A navigable location to route to in the application
  originalError: any;         // The original error that occurred
  showToast: boolean;         // A flag indicating whether or not to show a toast message
}

export class ApplicationError implements IApplicationError {

  public static readonly x: number = 10;

  private _errorCode: number;
  get errorCode(): number {
    return this._errorCode;
  }

  private _errorHeader: string = 'errorHeader';
  get errorHeader(): string {
    return this._errorHeader;
  }
  set errorHeader(value: string) {
    this._errorHeader = value;
  }

  private _errorMessage: string;
  get errorMessage(): string {
    return this._errorMessage;
  }

  private _displayMessage: string;
  get displayMessage(): string {
    return this._displayMessage;
  }

  private _source: string | null = null;
  get source(): string | null {
    return this._source;
  }

  private _isFatal: boolean = false;
  get isFatal(): boolean {
    return this._isFatal;
  }

  private _statusCode: number;
  get statusCode(): number {
    return this._statusCode;
  }

  private _detailedMessage: string;
  get detailedMessage(): string {
    return this._detailedMessage;
  }

  private _redirectTo: string | null | undefined = null;
  get redirectTo(): string | null | undefined {
    return this._redirectTo;
  }

  private _originalError: any = null;
  get originalError(): any {
    return this._originalError;
  }

  private _showToast: boolean = true;
  get showToast(): boolean {
    return this._showToast;
  }

  private constructor() {
    this._errorCode = 0;
    this._errorMessage = '';
    this._displayMessage = '';
    this._statusCode = 0;
    this._detailedMessage = '';
  }

  static create(
    errorCode: number,
    source?: string,
    isFatal?: boolean,
    originalError?: any,
    detailedMessage?: string,
    displayMessage?: string,
    statusCode?: number,
    redirectTo?: string,
    showToast?: boolean
  ): ApplicationError {
    const error: ApplicationError = new ApplicationError();

    const errorMessage = ErrorMessage.getErrorMessage(errorCode);

    error._errorMessage = errorMessage.errorMessage;
    error._displayMessage = displayMessage ?? errorMessage.translationKey;
    error._source = source ?? '';
    error._isFatal = isFatal ?? false;
    error._statusCode = statusCode ?? 0;
    error._detailedMessage = detailedMessage ?? errorMessage.translationKey;
    error._redirectTo = redirectTo;
    error._originalError = originalError;

    if (showToast !== undefined) error._showToast = showToast;

    // the error that occurred is an http error
    if (originalError && originalError instanceof HttpErrorResponse) {
      error._statusCode = originalError.status;
      error._detailedMessage = originalError.message;
      error._originalError = originalError.error;
    } else {
      error._statusCode = originalError?.errorNumber || -1;
      error._detailedMessage = originalError?.errorDetail || 'The program has encountered an error that was not anticipated.';
    }

    return error;
  }
}
